import React, { useState, useEffect, useContext, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ApiBaseUrlContext } from '../App';
import './Login.css';
import '../forms.css';
import { ReactComponent as MntLogo } from '../assets/svg/cimco-mnt-logo.svg';
import { LoadingContext } from '../context/LoadingContext';
import Spinner from './Spinner'; // Import the spinner component

const Login = ({ setAuthToken }) => {
  // Using both isLoading and setIsLoading from context
  const { isLoading, setIsLoading } = useContext(LoadingContext);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showLicensePopup, setShowLicensePopup] = useState(false);
  const [newLicense, setNewLicense] = useState('');
  const [showLicenseField, setShowLicenseField] = useState(false);
  const [licenseInfo, setLicenseInfo] = useState(null);
  const [licenseKeyFromSettings, setLicenseKeyFromSettings] = useState('');
  const navigate = useNavigate();
  const apiBaseUrl = useContext(ApiBaseUrlContext);
  const { t } = useTranslation();
  const [showMaintenancePopup, setShowMaintenancePopup] = useState(false);
  const [maintenanceExpiryDate, setMaintenanceExpiryDate] = useState('');
  const [loginAllowed, setLoginAllowed] = useState(true);

  useEffect(() => {
    document.body.classList.add('dark-mode');
    // Uncomment the line below to force the license popup for testing.
    // setShowLicensePopup(true);
  }, []);

  // Function to validate the license key using the Cryptolens API.
  // It checks whether the key is valid and not blocked.
  const fetchLicenseInformation = useCallback(async (key) => {
    if (!key) return;
    console.log('[DEBUG] Sending license key to backend:', key);

    try {
      const response = await axios.post(`${apiBaseUrl}/mnt/api/licenses/validate`, {
        licenseKey: key,
      });
  
      if (response.data.valid && response.data.licenseInfo) {
        const code = response.data.code;
      
        if (code === 'VERSION_TOO_NEW') {
          setError(`Your license does not cover this version.`);
          return;
        }
      
        if (code === 'MAINTENANCE_EXPIRED') {
          setMaintenanceExpiryDate(response.data.expiredAt.split('T')[0]);
          setShowMaintenancePopup(true);
          setLicenseInfo(response.data.licenseInfo);
          return;
        }
      
        // Fully valid, no warnings
        setLicenseInfo(response.data.licenseInfo);
        setShowLicensePopup(false);
      }
      
    } catch (error) {
      console.error("Error validating license through backend:", error);
      setShowLicensePopup(true);
    }
  }, [apiBaseUrl]);

  const isVersionAllowed = async (licenseInfo) => {
    try {
      const versionData = await axios.get('/mnt/version.json');
      const currentVersion = versionData.data.currentVersion;
      const releases = versionData.data.releases;
  
      const f1 = licenseInfo.f1 === 1;
      const expiresAt = new Date(licenseInfo.expires_at);
  
      if (!f1 && expiresAt < new Date()) {
        const allowedReleases = releases
          .filter(r => new Date(r.releaseDate) <= expiresAt)
          .sort((a, b) => new Date(b.releaseDate) - new Date(a.releaseDate));
  
        const latestAllowedVersion = allowedReleases[0]?.version;
        if (latestAllowedVersion && currentVersion > latestAllowedVersion) {
          alert(`Your perpetual license expired on ${expiresAt.toISOString().split('T')[0]}. You can only use version ${latestAllowedVersion} or older.`);
          return false;
        }
      }
  
      return true;
    } catch (error) {
      console.error("Error checking version validity:", error);
      return true; // default to allow if check fails
    }
  };
  

  // useEffect to check the license key from your settings API when the component loads.
  useEffect(() => {
    const checkStoredLicense = async () => {
      try {
        const { data } = await axios.get(`${apiBaseUrl}/mnt/api/settings`);
        const { licenseKey } = data;
        setLicenseKeyFromSettings(licenseKey);
  
        const licensePattern = /^[A-Za-z0-9]{5}(?:-[A-Za-z0-9]{5}){3}$/;
  
        if (licenseKey && licensePattern.test(licenseKey)) {
          try {
            const verifyRes = await axios.get(`${apiBaseUrl}/mnt/api/licenses/verify`);
            console.log('[DEBUG] verifyRes from backend:', verifyRes.data);
            if (!verifyRes.data.valid) {
              const code = verifyRes.data.code;
              if (code === 'VERSION_TOO_NEW') {
                setError(t('Login.VersionTooNew') || 'This version is not covered by your license.');
                setLoginAllowed(false);
                return; // ❗️block login completely
              }
            
              // fallback if it's some other failure
              console.warn('Stored license not valid:', verifyRes.data.message);
              fetchLicenseInformation(licenseKey);
              return;
            }
            
            if (verifyRes.data.code === 'MAINTENANCE_EXPIRED') {
              setMaintenanceExpiryDate(verifyRes.data.expiredAt.split('T')[0]);
              setShowMaintenancePopup(true);
              setLicenseInfo(verifyRes.data.licenseInfo);
              return;
            }
            
            const allowed = await isVersionAllowed(verifyRes.data.licenseInfo);
            if (!allowed) {
              setError('Version too new for this expired license.');
              setLoginAllowed(false);
              return;
            }
            
            setLicenseInfo(verifyRes.data.licenseInfo);
            setShowLicensePopup(false);
            


          } catch (verifyErr) {
            console.error('Error verifying stored license:', verifyErr);
            fetchLicenseInformation(licenseKey);
            return;
          }
        } else {
          setShowLicensePopup(true);
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
        setShowLicensePopup(true);
      }
    };
  
    checkStoredLicense(); // ← call the async function
  }, [apiBaseUrl, fetchLicenseInformation]);
  
  const handleLogin = async (e) => {
    e.preventDefault();

    if (!loginAllowed) {
      console.warn('🚫 Login blocked due to invalid license.');
      return; // 🛑 Stop early
    }

    setIsLoading(true); // Start the spinner

    try {
        const response = await axios.post(`${apiBaseUrl}/mnt/api/login`, { username, password });
        const { token } = response.data;

        setAuthToken(token);
        localStorage.setItem('token', token);

        // Check if a redirect URL was stored in sessionStorage
        let redirectUrl = sessionStorage.getItem('redirectAfterLogin');

        // If no redirect URL in sessionStorage, check query params
        if (!redirectUrl) {
            const params = new URLSearchParams(window.location.search);
            redirectUrl = params.get('redirect') ? decodeURIComponent(params.get('redirect')) : null;
        }

        console.log(`Redirecting user to: ${redirectUrl || '/'}`);

        // Navigate to the correct page
        setTimeout(() => {
          navigate(
            redirectUrl && (redirectUrl.startsWith('/mnt/fault-error') || redirectUrl.startsWith('/fault-error'))
              ? redirectUrl
              : '/'
          );
          
        }, 500);

        // Clear the stored redirect URL after use
        sessionStorage.removeItem('redirectAfterLogin');
    } catch (error) {
        console.error('Full error object:', error);

        if (error.response) {
            const { code } = error.response.data;
            console.log(`Extracted error code: [${code}]`);

            if (code === 'LICENSE_NOT_FOUND' || code === 'LICENSE_BLOCKED') {
                setIsLoading(false);
                setShowLicenseField(true);
                console.log('License error detected; showing license field.');
            } else if (code === 'LICENSE_TOO_MANY_USERS') {
                setError("Too many users, upgrade license");
            } else {
                setError(t('Login.InvalidCredentials'));
            }
        } else {
            setError(t('Login.InvalidCredentials'));
        }
    } finally {
        setIsLoading(false); // Always hide the spinner
    }
  };



  const handleLicenseSubmit = async () => {
    try {
      const token = localStorage.getItem('token');
  
      // Step 1: Save the key in mt_settings
      await axios.post(
        `${apiBaseUrl}/mnt/api/settings/update-license`,
        { licenseKey: newLicense },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );
  
      // Step 2: Validate + store full license in mt_license_info
      const validationResponse = await axios.post(
        `${apiBaseUrl}/mnt/api/licenses/validate`,
        { licenseKey: newLicense }
      );
  
      if (validationResponse.data.valid) {
        setLicenseInfo(validationResponse.data.licenseInfo);
        setShowLicensePopup(false);
        setError('');
        alert('License key validated and saved successfully.');
      } else {
        setError('License key is invalid.');
      }
  
    } catch (error) {
      console.error('Error submitting license key:', error);
      setError('Failed to submit and validate license key.');
    }
  };

   
 

  return (
    <div className="login">
      {/* Render the Spinner if isLoading is true */}
      {isLoading && <Spinner />}

      {/* License Popup with a higher z-index */}
      {showLicensePopup && (
        <div className="license-popup">
          <p style={{ color: 'black', fontWeight: 'normal' }}>
            Please enter a valid license key to continue using the application.
          </p>
          <p>{t('Enter new license key')}</p>
          <input 
            className="form-login-control"
            type="text"
            value={newLicense}
            onChange={(e) => setNewLicense(e.target.value)}
          />
          <button className="login-button" onClick={handleLicenseSubmit}>
            {t('Save License')}
          </button>
        </div>
      )}

      {showMaintenancePopup && (
        <div className="license-popup">
          <p>{t('Login.MaintenanceExpiredTitle', { date: maintenanceExpiryDate })}</p>
          <p>{t('Login.MaintenanceExpiredDescription')}</p>
          <button className="login-button" onClick={() => setShowMaintenancePopup(false)}>
            {t('Login.ContinueButton')}
          </button>
        </div>
      )}

      <div className="login-box">
        <div className="login-box__logo">
          <MntLogo />
        </div>
        <form onSubmit={handleLogin}>
          <div className="entry-item entry-item__full-width">
            <label className="entry-label">{t('Login.UsernameLabel')}</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              className="form-login-control"
            />
          </div>
          <div className="entry-item entry-item__full-width">
            <label className="entry-label">{t('Login.PasswordLabel')}</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="form-login-control"
            />
          </div>

          {error && <div className="login-box__error-msg">{error}</div>}
          {showLicenseField && (
            <div className="entry-item entry-item__full-width">
              <label className="entry-label">{t('Enter new license key')}</label>
              <input 
                type="text"
                value={newLicense}
                onChange={(e) => setNewLicense(e.target.value)}
                className="form-login-control"
              />
              <button 
                type="button" 
                onClick={handleLicenseSubmit} 
                className="button--full-width button--secondary"
              >
                {t('Save License')}
              </button>
            </div>
          )}
		<div className="entry-item entry-item__full-width">													 
          <button type="submit" className="button--full-width button--grey">
            {t('Login.LoginButton')}
          </button>
		</div>		
        </form>
      </div>
    </div>
  );
};

export default Login;
